import { Flex, Text, Badge, Box, Tooltip, Button } from '@chakra-ui/react';
import { formatFeature } from '../../../utils/subscriptionUtils';
import PlanCardFeatures from './PlanCardFeatures';
import {
  PLAN_CARD_INCLUDED_FEATURES,
  PLAN_CARD_HIDDEN_FEATURE_KEYS,
} from './planFeaturesConfig';
import {
  PLAN_CARD_PRICE_ROW_MIN_H,
  PLAN_CARD_PRICE_BLOCK_MIN_H,
  PLAN_CARD_BORDER,
} from './planCardLayout';

const SubscriptionOption = ({
  label,
  price,
  compareAtPrice,
  planId,
  priceId,
  billingPriceFormatted,
  billedPeriodCaption,
  features,
  finalPayment,
  setSelectedPlan,
  setCancelPlan,
  interval = 'month',
  readOnly = false,
  isCurrentPlan = false,
  disabled = false,
  subscription,
  planRank,
  isUpgrade = false,
  isDowngrade = false,
  onOpenUpdatePlanModal,
}) => {
  const isMonthlyToYearly = () => {
    if (!subscription?.plan) return false;

    const currentPlanName = subscription.plan.name;
    const currentInterval = subscription.plan.interval;

    return (
      label === currentPlanName &&
      currentInterval === 'month' &&
      interval === 'year'
    );
  };

  const getPlanComparison = () => {
    const thisRank = parseInt(planRank, 10) || 0;
    const currentPlanRank = parseInt(subscription?.plan?.rank, 10) || 0;

    return {
      thisRank,
      currentPlanRank,
      isDifferentRank: thisRank !== currentPlanRank,
      isHigherRank: thisRank > currentPlanRank,
      isLowerRank: thisRank < currentPlanRank,
    };
  };

  const getPlanTypeComparison = () => {
    if (isUpgrade) {
      return { type: 'upgrade', text: 'Upgrade' };
    }

    if (isDowngrade) {
      return { type: 'downgrade', text: 'Downgrade' };
    }

    const comparison = getPlanComparison();

    if (comparison.isHigherRank) {
      return { type: 'upgrade', text: 'Upgrade' };
    } else if (comparison.isLowerRank) {
      return { type: 'downgrade', text: 'Downgrade' };
    }

    return null;
  };

  const typeComparison = isCurrentPlan ? null : getPlanTypeComparison();

  const handleSelectPlan = () => {
    if (finalPayment && setCancelPlan) {
      setCancelPlan(true);
    }

    const selectedData = {
      planId: planId,
      priceId: priceId,
      label: label,
      price: billingPriceFormatted ?? price,
      interval: interval,
    };

    setSelectedPlan?.(selectedData);
    onOpenUpdatePlanModal?.();
  };

  const featureLabels = [
    ...(features || [])
      .filter(
        (f) => f.limit != null && !PLAN_CARD_HIDDEN_FEATURE_KEYS.includes(f.key)
      )
      .map(formatFeature),
    ...PLAN_CARD_INCLUDED_FEATURES,
  ];

  const billingLine = (
    <Text textStyle="bodySmall" color="textGrey">
      Billed {interval === 'year' ? 'annually' : 'monthly'}
    </Text>
  );

  const showAnnualCompare =
    interval === 'year' && Boolean(compareAtPrice && price);

  const perMoSuffix = (
    <Text as="span" textStyle="heading5" color="textGrey" fontWeight="medium">
      /mo
    </Text>
  );

  const cadenceDetail =
    billedPeriodCaption && interval === 'year' ? (
      <Text textStyle="bodySmall" color="textGrey">
        {billedPeriodCaption}
      </Text>
    ) : (
      billingLine
    );

  const planBody = (
    <Flex direction="column" gap={3} alignItems="flex-start" w="full" h="full">
      <Flex direction="column" gap={1} w="full">
        <Text textStyle="heading4">{label}</Text>
        <Flex
          direction="column"
          alignItems="flex-start"
          gap={0.5}
          w="full"
          minH={PLAN_CARD_PRICE_BLOCK_MIN_H}
        >
          {showAnnualCompare ? (
            <>
              <Flex
                direction="row"
                alignItems="baseline"
                flexWrap="wrap"
                gap={3}
                rowGap={1}
                minH={PLAN_CARD_PRICE_ROW_MIN_H}
              >
                <Text
                  textStyle="heading2"
                  fontWeight={isDowngrade ? 'bold' : 'normal'}
                >
                  {price}
                </Text>
                <Text>
                  <Text
                    as="span"
                    textStyle="heading3"
                    textDecoration="line-through"
                    color="textGrey"
                  >
                    {compareAtPrice}
                  </Text>
                  {perMoSuffix}
                </Text>
              </Flex>
              {cadenceDetail}
            </>
          ) : (
            <>
              <Flex
                direction="row"
                alignItems="baseline"
                gap={1}
                minH={PLAN_CARD_PRICE_ROW_MIN_H}
              >
                <Text
                  textStyle="heading2"
                  fontWeight={isDowngrade ? 'bold' : 'normal'}
                >
                  {price || ''}
                  {price ? perMoSuffix : null}
                </Text>
              </Flex>
              {cadenceDetail}
            </>
          )}
        </Flex>
      </Flex>

      {featureLabels.length > 0 && (
        <PlanCardFeatures
          heading="What's included"
          features={featureLabels}
          isMuted={isDowngrade}
        />
      )}

      {isCurrentPlan ? (
        <Button variant="secondary" size="sm" w="full" isDisabled>
          Select plan
        </Button>
      ) : isDowngrade ? (
        <Button
          variant="secondary"
          size="sm"
          w="full"
          isDisabled
          marginTop="auto"
        >
          Select plan
        </Button>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          w="full"
          marginTop="auto"
          onClick={(e) => {
            e.stopPropagation();
            handleSelectPlan();
          }}
        >
          Select plan
        </Button>
      )}
    </Flex>
  );

  if (isCurrentPlan) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        border="1.5px solid #D9D9D9"
        borderRadius="15px"
        p={8}
        position="relative"
        bg="rgba(255, 255, 255, 0.15)"
      >
        <Badge
          position="absolute"
          top="0"
          transform="translateY(-50%)"
          border="1.5px solid orangePrimary"
          bg="#D9D9D9"
          color="darkGrey2"
          fontSize="sm"
          px={3}
          py={1.5}
          borderRadius="full"
        >
          Current Plan
        </Badge>
        {planBody}
      </Flex>
    );
  }

  if (isDowngrade) {
    return (
      <Tooltip
        label="Please contact support to downgrade your plan"
        placement="top"
        hasArrow
      >
        <Box
          h="full"
          pointerEvents="none"
          aria-disabled={true}
          role="group"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onKeyDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          style={{
            cursor: 'not-allowed',
            position: 'relative',
            filter: 'grayscale(20%)',
          }}
        >
          <Flex
            direction="column"
            alignItems="center"
            border="1.5px solid #D9D9D9"
            width="100%"
            minW="260px"
            height="full"
            borderRadius="15px"
            p={8}
            position="relative"
            opacity={0.7}
            borderColor="gray.500"
            bg="transparent"
          >
            <Badge
              position="absolute"
              top="0"
              transform="translateY(-40%)"
              border="1.5px solid #D9D9D9"
              color="primaryDark"
              variant="white"
              fontSize="sm"
              px={3}
              py={1.5}
              borderRadius="full"
              boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
            >
              Contact Support to Downgrade
            </Badge>
            {planBody}
          </Flex>

          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex="2"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Flex
      direction="column"
      alignItems="center"
      width="100%"
      minW="260px"
      h="full"
      borderRadius="15px"
      p={8}
      position="relative"
      _hover={{
        bg:
          disabled || isDowngrade ? 'transparent' : 'rgba(255, 255, 255, 0.05)',
      }}
      onClick={(e) => {
        if (disabled || readOnly) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        handleSelectPlan(e);
      }}
      opacity={disabled ? 0.6 : 1}
      {...PLAN_CARD_BORDER}
      bg={isUpgrade ? 'white' : 'transparent'}
    >
      {/* {typeComparison && (
        <Badge
          position="absolute"
          top="0"
          transform="translateY(-40%)"
          color="white"
          bg="linear-gradient(180deg, #FF9554 16%, #FF4935 100%)"
          fontSize="sm"
          px={3}
          py={1.5}
          borderRadius="full"
        >
          {typeComparison.text}
        </Badge>
      )} */}

      {/* {isMonthlyToYearly() && (
        <Badge
          position="absolute"
          top="0"
          transform="translateY(-40%)"
          right="20px"
          variant="white"
          fontSize="xs"
          px={3}
          py={1.5}
          borderRadius="full"
          fontWeight="bold"
          boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
        >
          Monthly → Yearly
        </Badge>
      )} */}

      {planBody}
    </Flex>
  );
};

export default SubscriptionOption;
